import { query, getDocs, serverTimestamp } from '../firebase.config';
import { enforcedAddDoc as addDoc } from './enforcedTracking';
import { getCollectionForCompany } from './dataAdapter';
import { timestampToDate } from './dateUtils';

/** FY label "2025-26" → { start: Apr 1 2025, end: Mar 31 2026 } */
export function getFinancialYearDateRange(targetYear) {
  const startYear = parseInt(String(targetYear).split('-')[0], 10);
  if (!startYear || Number.isNaN(startYear)) {
    const now = new Date();
    return { yearStart: new Date(now.getFullYear(), 3, 1), yearEnd: new Date(now.getFullYear() + 1, 2, 31, 23, 59, 59, 999) };
  }
  return {
    yearStart: new Date(startYear, 3, 1),
    yearEnd: new Date(startYear + 1, 2, 31, 23, 59, 59, 999),
  };
}

export function parseDocDate(value) {
  if (!value) return null;
  const fromTs = timestampToDate(value);
  if (fromTs && !Number.isNaN(fromTs.getTime())) return fromTs;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function isDateInRange(date, yearStart, yearEnd) {
  if (!date) return false;
  return date >= yearStart && date <= yearEnd;
}

export function billBelongsToParty(bill, partyId) {
  if (!bill || !partyId) return false;
  const pid = bill.partyId || bill.party || bill.customerId || bill.supplierId;
  return pid === partyId;
}

export function paymentBelongsToParty(payment, partyId) {
  if (!payment || !partyId) return false;
  return (payment.partyId || payment.party) === partyId;
}

/** Opening-balance ledger rows and internal allocations must not affect FY roll-forward. */
export function isExcludedFromFyPaymentCalc(payment) {
  if (!payment) return true;
  if (payment.isOpeningBalanceEntry || payment.paymentType === 'opening-balance') return true;
  if (payment.paymentType === 'advance-allocation') return true;
  return false;
}

export function isCustomerReceiptPayment(payment) {
  if (isExcludedFromFyPaymentCalc(payment)) return false;
  const recNo = String(payment.receiptNumber || payment.number || '').toUpperCase();
  if (recNo.startsWith('PRI')) return true;
  const t = String(payment.type || '').toLowerCase();
  return t === 'receipts' || t === 'invoice' || t === 'challan' || t === 'quotation';
}

export function isSupplierPaymentPayment(payment) {
  if (isExcludedFromFyPaymentCalc(payment)) return false;
  const recNo = String(payment.receiptNumber || payment.number || '').toUpperCase();
  if (recNo.startsWith('PRP') || recNo.startsWith('PRB')) return true;
  const t = String(payment.type || '').toLowerCase();
  return t === 'payments' || t === 'purchase';
}

/**
 * Sum FY sales, purchases, and payments for one party using company-scoped collections.
 */
export async function calculatePartyFyTotals(db, ctx, party, targetYear) {
  const { companyId, appId, userId } = ctx;
  if (!db || !party?.id || (!companyId && !(appId && userId))) {
    return null;
  }

  const { yearStart, yearEnd } = getFinancialYearDateRange(targetYear);
  let totalSales = 0;
  let totalPurchases = 0;
  let totalPayments = 0;
  let totalPurchasePayments = 0;

  // Sales bills
  try {
    const salesRef = getCollectionForCompany(db, { companyId, appId, userId }, 'salesBills');
    if (salesRef) {
      const salesSnap = await getDocs(query(salesRef));
      salesSnap.forEach((docSnap) => {
        const bill = docSnap.data();
        if (!billBelongsToParty(bill, party.id)) return;
        const billDate = parseDocDate(bill.invoiceDate || bill.date || bill.billDate);
        if (!isDateInRange(billDate, yearStart, yearEnd)) return;
        totalSales += parseFloat(bill.totalAmount ?? bill.amount ?? 0) || 0;
      });
    }
  } catch (err) {
    console.warn('FY calc: sales bills', err);
  }

  // Purchase bills
  try {
    const purchaseRef = getCollectionForCompany(db, { companyId, appId, userId }, 'purchaseBills');
    if (purchaseRef) {
      const purchaseSnap = await getDocs(query(purchaseRef));
      purchaseSnap.forEach((docSnap) => {
        const bill = docSnap.data();
        if (!billBelongsToParty(bill, party.id)) return;
        const billDate = parseDocDate(bill.billDate || bill.date || bill.invoiceDate);
        if (!isDateInRange(billDate, yearStart, yearEnd)) return;
        totalPurchases += parseFloat(bill.totalAmount ?? bill.amount ?? 0) || 0;
      });
    }
  } catch (err) {
    console.warn('FY calc: purchase bills', err);
  }

  // Payments (PRI + PRP in one collection)
  try {
    const paymentsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'payments');
    if (paymentsRef) {
      const paymentsSnap = await getDocs(query(paymentsRef));
      paymentsSnap.forEach((docSnap) => {
        const payment = docSnap.data();
        if (!paymentBelongsToParty(payment, party.id)) return;
        const paymentDate = parseDocDate(payment.paymentDate || payment.date);
        if (!isDateInRange(paymentDate, yearStart, yearEnd)) return;
        const amount = parseFloat(payment.totalAmount ?? payment.amount ?? 0) || 0;
        if (isCustomerReceiptPayment(payment)) {
          totalPayments += amount;
        } else if (isSupplierPaymentPayment(payment)) {
          totalPurchasePayments += amount;
        }
      });
    }
  } catch (err) {
    console.warn('FY calc: payments', err);
  }

  const previousBalance = parseFloat(party.openingBalance) || 0;
  const calculatedBalance =
    previousBalance + totalSales - totalPayments - totalPurchases + totalPurchasePayments;

  return {
    previousBalance,
    totalSales,
    totalPurchases,
    totalPayments,
    totalPurchasePayments,
    calculatedBalance,
    calculationDate: new Date().toISOString(),
  };
}

/** Generate next PRI or PRP receipt number for opening-balance entries. */
export async function generateOpeningBalanceReceiptNumber(db, ctx, isReceipt) {
  const { companyId, appId, userId } = ctx;
  const currentYear = new Date().getFullYear();
  const financialYear = `${currentYear.toString().slice(-2)}-${(currentYear + 1).toString().slice(-2)}`;
  const paymentType = isReceipt ? 'I' : 'P';
  const prefix = `PR${paymentType}${financialYear}`;

  try {
    const paymentsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'payments');
    if (!paymentsRef) return `${prefix}/001`;
    const paymentsSnapshot = await getDocs(query(paymentsRef));
    const existingSequences = new Set();
    const regexPattern = new RegExp(`PR${paymentType}${financialYear.replace('-', '\\-')}\\/(\\d+)`);
    paymentsSnapshot.forEach((docSnap) => {
      const payment = docSnap.data();
      if (!payment.receiptNumber) return;
      const match = payment.receiptNumber.match(regexPattern);
      if (match) existingSequences.add(parseInt(match[1], 10));
    });
    let nextSequence = existingSequences.size > 0 ? Math.max(...existingSequences) + 1 : 1;
    let receiptNumber = `${prefix}/${nextSequence.toString().padStart(3, '0')}`;
    let attempts = 0;
    while (existingSequences.has(nextSequence) && attempts < 100) {
      nextSequence += 1;
      receiptNumber = `${prefix}/${nextSequence.toString().padStart(3, '0')}`;
      attempts += 1;
    }
    return receiptNumber;
  } catch (err) {
    console.error('Error generating opening balance receipt number:', err);
    const timestampSeq = Math.floor(Date.now() / 1000) % 1000;
    return `${prefix}/${timestampSeq.toString().padStart(3, '0')}`;
  }
}

/** Create PRI/PRP ledger row when opening balance is set or changed. */
export async function createOpeningBalancePaymentEntry(
  db,
  ctx,
  { partyId, partyName, openingBalanceAmount, partyTypeValue, isNewParty }
) {
  if (!openingBalanceAmount || openingBalanceAmount === 0) return;

  const { companyId, appId, userId } = ctx;
  const isReceipt = openingBalanceAmount > 0;
  const receiptNumber = await generateOpeningBalanceReceiptNumber(db, ctx, isReceipt);
  const paymentType = isReceipt ? 'receipts' : 'payments';

  const paymentData = {
    receiptNumber,
    paymentDate: serverTimestamp(),
    partyId,
    partyName,
    totalAmount: Math.abs(openingBalanceAmount),
    paymentMode: 'Opening Balance',
    reference: isNewParty ? 'Opening Balance - New Party' : 'Opening Balance - Updated',
    notes: `Opening balance ${isNewParty ? 'set' : 'updated'} for party: ${partyName} (${partyTypeValue}). Amount: ₹${Math.abs(openingBalanceAmount).toLocaleString('en-IN')} (${isReceipt ? 'Receivable - PRI' : 'Payable - PRP'})`,
    type: paymentType,
    paymentType: 'opening-balance',
    allocations: [],
    remainingAmount: 0,
    openingBalanceAmount,
    isOpeningBalanceEntry: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const paymentsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'payments');
  if (!paymentsRef) throw new Error('Unable to access payments collection');
  await addDoc(paymentsRef, paymentData);
}
